import React, { createContext, useContext, useState, useCallback } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete, apiUpload } from '../utils/api';
import { modelServiceFetch, modelServicePost, modelServiceUpload } from '../utils/modelService';

const AitContext = createContext();

export const useAit = () => {
  const context = useContext(AitContext);
  if (!context) {
    throw new Error('useAit must be used within an AitProvider');
  }
  return context;
};

export const AitProvider = ({ children }) => {
  const [properties, setProperties] = useState([]);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [currentBuilding, setCurrentBuilding] = useState(null);
  const [units, setUnits] = useState([]);
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Chat state
  const [chats, setChats] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);

  // Portfolio analytics state
  const [portfolioData, setPortfolioData] = useState(null);
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  // ── Properties ──

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet('/ait_properties');
      const data = await response.json();
      setProperties(data.properties || []);
      return data.properties;
    } catch (err) {
      setError(err.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const createProperty = useCallback(async (propertyData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiPost('/ait_properties', propertyData);
      const data = await response.json();
      if (response.ok) {
        setProperties(prev => [data.property, ...prev]);
        return { success: true, property: data.property };
      }
      setError(data.errors?.join(', ') || 'Failed to create property');
      return { success: false, error: data.errors };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProperty = useCallback(async (id, propertyData) => {
    setError(null);
    try {
      const response = await apiPatch(`/ait_properties/${id}`, propertyData);
      const data = await response.json();
      if (response.ok) {
        setProperties(prev => prev.map(p => p.id === id ? data.property : p));
        if (currentProperty?.id === id) setCurrentProperty(data.property);
        return { success: true, property: data.property };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [currentProperty]);

  const deleteProperty = useCallback(async (id) => {
    setError(null);
    try {
      const response = await apiDelete(`/ait_properties/${id}`);
      if (response.ok) {
        setProperties(prev => prev.filter(p => p.id !== id));
        if (currentProperty?.id === id) setCurrentProperty(null);
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      setError(err.message);
      return { success: false, error: err.message };
    }
  }, [currentProperty]);

  const fetchProperty = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet(`/ait_properties/${id}`);
      const data = await response.json();
      if (response.ok) {
        setCurrentProperty(data.property);
        return data;
      }
      setError(data.error);
      return null;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Buildings ──

  const createBuilding = useCallback(async (propertyId, buildingData) => {
    setError(null);
    try {
      const response = await apiPost(`/ait_properties/${propertyId}/buildings`, buildingData);
      const data = await response.json();
      if (response.ok) return { success: true, building: data.building };
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const fetchBuilding = useCallback(async (propertyId, buildingId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/buildings/${buildingId}`);
      const data = await response.json();
      if (response.ok) {
        setCurrentBuilding(data.building);
        setUnits(data.units || []);
        return data;
      }
      setError(data.error);
      return null;
    } catch (err) {
      setError(err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteBuilding = useCallback(async (propertyId, buildingId) => {
    try {
      const response = await apiDelete(`/ait_properties/${propertyId}/buildings/${buildingId}`);
      if (response.ok) return { success: true };
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updateBuilding = useCallback(async (propertyId, buildingId, data) => {
    try {
      const response = await apiPatch(`/ait_properties/${propertyId}/buildings/${buildingId}`, data);
      const result = await response.json();
      if (response.ok) return { success: true, building: result.building };
      return { success: false, error: result.errors?.join(', ') || result.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Units ──

  const fetchUnits = useCallback(async (buildingId) => {
    try {
      const response = await apiGet(`/ait_buildings/${buildingId}/units`);
      const data = await response.json();
      if (response.ok) {
        setUnits(data.units || []);
        return data.units;
      }
      return [];
    } catch (err) {
      return [];
    }
  }, []);

  const createUnit = useCallback(async (buildingId, unitData) => {
    try {
      const response = await apiPost(`/ait_buildings/${buildingId}/units`, unitData);
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => [...prev, data.unit]);
        return { success: true, unit: data.unit };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const updateUnit = useCallback(async (buildingId, unitId, unitData) => {
    try {
      const response = await apiPatch(`/ait_buildings/${buildingId}/units/${unitId}`, unitData);
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => prev.map(u => u.id === unitId ? data.unit : u));
        return { success: true, unit: data.unit };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const deleteUnit = useCallback(async (buildingId, unitId) => {
    try {
      const response = await apiDelete(`/ait_buildings/${buildingId}/units/${unitId}`);
      if (response.ok) {
        setUnits(prev => prev.filter(u => u.id !== unitId));
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const bulkImportUnits = useCallback(async (buildingId, unitsData) => {
    try {
      const response = await apiPost(`/ait_buildings/${buildingId}/units/bulk`, { units: unitsData });
      const data = await response.json();
      if (data.units) setUnits(data.units);
      return { success: true, imported: data.imported, errors: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Designation operations ──

  const redesignateUnit = useCallback(async (buildingId, unitId, newDesignation, reason, notes) => {
    try {
      const response = await apiPatch(`/ait_buildings/${buildingId}/units/${unitId}/redesignate`, {
        new_designation: newDesignation, reason, notes
      });
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => prev.map(u => u.id === unitId ? data.unit : u));
        setCalculation(data.calculation);
        return { success: true, unit: data.unit, calculation: data.calculation };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const convertToMarketRate = useCallback(async (buildingId, unitId, reason, notes) => {
    try {
      const response = await apiPatch(`/ait_buildings/${buildingId}/units/${unitId}/convert_to_market_rate`, {
        reason, notes
      });
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => prev.map(u => u.id === unitId ? data.unit : u));
        setCalculation(data.calculation);
        return { success: true };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const convertToQualified = useCallback(async (buildingId, unitId, designation, reason, notes) => {
    try {
      const response = await apiPatch(`/ait_buildings/${buildingId}/units/${unitId}/convert_to_qualified`, {
        designation, reason, notes
      });
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => prev.map(u => u.id === unitId ? data.unit : u));
        setCalculation(data.calculation);
        return { success: true };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const swapUnits = useCallback(async (buildingId, unitId, targetUnitId, designation, reason, notes) => {
    try {
      const response = await apiPost(`/ait_buildings/${buildingId}/units/${unitId}/swap`, {
        target_unit_id: targetUnitId, designation, reason, notes
      });
      const data = await response.json();
      if (response.ok) {
        setUnits(prev => prev.map(u => {
          if (u.id === unitId) return data.released_unit;
          if (u.id === targetUnitId) return data.qualified_unit;
          return u;
        }));
        setCalculation(data.calculation);
        return { success: true };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Calculation & Scenarios ──

  const calculateBuilding = useCallback(async (propertyId, buildingId) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/buildings/${buildingId}/calculate`);
      const data = await response.json();
      if (response.ok) {
        setCalculation(data.calculation);
        return data.calculation;
      }
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  const modelScenario = useCallback(async (propertyId, buildingId, changes) => {
    try {
      const response = await apiPost(`/ait_properties/${propertyId}/buildings/${buildingId}/scenario`, { changes });
      const data = await response.json();
      if (response.ok) return data.scenario;
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  const createSnapshot = useCallback(async (propertyId, buildingId, { snapshot_type } = {}) => {
    try {
      const body = snapshot_type ? { snapshot_type } : {};
      const response = await apiPost(`/ait_properties/${propertyId}/buildings/${buildingId}/snapshot`, body);
      const data = await response.json();
      if (response.ok) return { success: true, snapshot: data.snapshot };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Upload (file goes to model service, parsed data goes to Rails) ──

  const parseUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Step 1: Submit — returns 202 with task_id
      const submitRes = await modelServiceUpload('/ait/parse-upload', formData);
      const { task_id } = await submitRes.json();
      if (!task_id) return { success: false, error: 'No task ID returned' };

      // Step 2: Poll until done
      while (true) {
        await new Promise(r => setTimeout(r, 3000));
        const pollRes = await modelServiceFetch(`/ait/parse-upload/status/${task_id}`);
        const poll = await pollRes.json();
        if (poll.status === 'done') return poll.result;
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // TIC (Tenant Income Certification) PDF parsing
  const parseTicUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await modelServiceUpload('/ait/parse-tic', formData);
      const data = await response.json();
      return data;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const parseTicBatch = useCallback(async (files) => {
    const formData = new FormData();
    files.forEach(f => formData.append('files', f));

    try {
      const response = await modelServiceUpload('/ait/parse-tic-batch', formData);
      const data = await response.json();
      return data;
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const saveUpload = useCallback(async (propertyId, parsedDataOrFile, filename, fileType) => {
    try {
      let response;
      // If parsedDataOrFile is a File object, send as multipart with actual file
      if (parsedDataOrFile instanceof File) {
        const formData = new FormData();
        formData.append('file', parsedDataOrFile);
        formData.append('filename', parsedDataOrFile.name);
        formData.append('file_type', fileType);
        response = await apiUpload(`/ait_properties/${propertyId}/upload`, formData);
      } else {
        const body = { filename, file_type: fileType };
        if (parsedDataOrFile && Object.keys(parsedDataOrFile).length > 0) {
          body.parsed_data = parsedDataOrFile;
        }
        response = await apiPost(`/ait_properties/${propertyId}/upload`, body);
      }
      const data = await response.json();
      if (response.ok) return { success: true, upload: data.upload };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const deleteUpload = useCallback(async (propertyId, uploadId) => {
    try {
      const response = await apiDelete(`/ait_properties/${propertyId}/uploads/${uploadId}`);
      if (response.ok) return { success: true };
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const fetchLeasingReport = useCallback(async (propertyId) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/leasing_report`);
      const data = await response.json();
      if (response.ok) return { success: true, ...data };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // scope: 'property' | 'building' | 'unit'
  // ids: { propertyId, buildingId, unitId } — provide what's needed for the scope
  // filters: { dateFrom, dateTo } — optional date range
  const fetchHistory = useCallback(async (scope, ids, page = 1, filters = {}) => {
    try {
      let url;
      if (scope === 'property') url = `/ait_properties/${ids.propertyId}/history`;
      else if (scope === 'building') url = `/ait_properties/${ids.propertyId}/buildings/${ids.buildingId}/history`;
      else if (scope === 'unit') url = `/ait_buildings/${ids.buildingId}/units/${ids.unitId}/history`;
      else return null;

      const params = new URLSearchParams({ page });
      if (filters.dateFrom) params.set('date_from', filters.dateFrom);
      if (filters.dateTo) params.set('date_to', filters.dateTo);
      url += `?${params.toString()}`;

      const response = await apiGet(url);
      const data = await response.json();
      if (response.ok) return data;
      return null;
    } catch {
      return null;
    }
  }, []);

  const fetchPropertyAnalytics = useCallback(async (propertyId, months = 12) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/analytics?months=${months}`);
      const data = await response.json();
      if (response.ok) return data;
      return null;
    } catch {
      return null;
    }
  }, []);

  const importUnits = useCallback(async (propertyId, units) => {
    try {
      const response = await apiPost(`/ait_properties/${propertyId}/import_units`, { units });
      const data = await response.json();
      if (response.ok) return { success: true, ...data };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Chat ──

  const fetchChats = useCallback(async (propertyId) => {
    setCurrentChatId(null);
    setChatMessages([]);
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/chats`);
      const data = await response.json();
      if (response.ok) {
        setChats(data.chats || []);
        return data.chats;
      }
      setChats([]);
      return [];
    } catch (err) {
      setChats([]);
      return [];
    }
  }, []);

  const createChat = useCallback(async (propertyId, title) => {
    try {
      const response = await apiPost(`/ait_properties/${propertyId}/chats`, { title });
      const data = await response.json();
      if (response.ok) {
        setChats(prev => [data.chat, ...prev]);
        setCurrentChatId(data.chat.id);
        setChatMessages([]);
        return { success: true, chat: data.chat };
      }
      return { success: false, error: data.errors };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const fetchChatMessages = useCallback(async (propertyId, chatId) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/chats/${chatId}`);
      const data = await response.json();
      if (response.ok) {
        setCurrentChatId(chatId);
        setChatMessages(data.messages || []);
        return data;
      }
      return null;
    } catch (err) {
      return null;
    }
  }, []);

  const sendChatMessage = useCallback(async (propertyId, chatId, message, buildingId) => {
    setChatLoading(true);
    try {
      // Optimistically add user message
      const tempUserMsg = {
        id: `temp-${Date.now()}`,
        content: message,
        is_user: true,
        created_at: new Date().toISOString(),
      };
      setChatMessages(prev => [...prev, tempUserMsg]);

      const body = { message };
      if (buildingId) body.building_id = buildingId;
      const response = await apiPost(`/ait_properties/${propertyId}/chats/${chatId}/message`, body);
      const data = await response.json();

      if (response.ok) {
        // Replace temp message with real one and add AI response
        setChatMessages(prev => {
          const withoutTemp = prev.filter(m => m.id !== tempUserMsg.id);
          return [...withoutTemp, data.user_message, data.ai_message];
        });
        // Update chat list (last updated + title if generated)
        setChats(prev => prev.map(c =>
          c.id === chatId ? {
            ...c,
            message_count: (c.message_count || 0) + 2,
            updated_at: new Date().toISOString(),
            ...(data.updated_title ? { title: data.updated_title } : {})
          } : c
        ));
        return { success: true, ai_message: data.ai_message, scenarios: data.scenarios };
      }

      // Remove temp message on error
      setChatMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      return { success: false, error: data.error };
    } catch (err) {
      setChatMessages(prev => prev.filter(m => !String(m.id).startsWith('temp-')));
      return { success: false, error: err.message };
    } finally {
      setChatLoading(false);
    }
  }, []);

  const deleteChat = useCallback(async (propertyId, chatId) => {
    try {
      const response = await apiDelete(`/ait_properties/${propertyId}/chats/${chatId}`);
      if (response.ok) {
        setChats(prev => prev.filter(c => c.id !== chatId));
        if (currentChatId === chatId) {
          setCurrentChatId(null);
          setChatMessages([]);
        }
        return { success: true };
      }
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, [currentChatId]);

  // ── Over-Income Evaluation ──

  const evaluateOverIncome = useCallback(async (buildingId, unitId) => {
    try {
      const response = await apiGet(`/ait_buildings/${buildingId}/units/${unitId}/over_income`);
      const data = await response.json();
      if (response.ok) return { success: true, ...data.over_income };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Monthly Designations ──

  const fetchMonthlyDesignations = useCallback(async (buildingId, year) => {
    try {
      const response = await apiGet(`/ait_buildings/${buildingId}/monthly_designations?year=${year}`);
      if (response.ok) return await response.json();
      return null;
    } catch {
      return null;
    }
  }, []);

  const updateMonthlyDesignations = useCallback(async (buildingId, updates) => {
    try {
      const response = await apiPatch(`/ait_buildings/${buildingId}/monthly_designations`, { updates });
      const data = await response.json();
      if (response.ok) return { success: true, ...data };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const carryForwardDesignations = useCallback(async (buildingId, year) => {
    try {
      const response = await apiPost(`/ait_buildings/${buildingId}/monthly_designations/carry_forward`, { year });
      const data = await response.json();
      if (response.ok) return { success: true, carried: data.carried, year: data.year };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Income Limit Lookup ──

  const lookupIncomeLimit = useCallback(async ({ address, state, county, designation, household_size, bedroom_count }) => {
    try {
      const response = await modelServicePost('/ait/income-limit-lookup', { address, state, county, designation, household_size, bedroom_count });
      const data = await response.json();
      if (response.ok && data.income_limit) {
        return { success: true, ...data };
      }
      return { success: false, error: data.error || 'Could not find income limits' };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Snapshots ──

  const fetchSnapshots = useCallback(async (buildingId) => {
    try {
      const response = await apiGet(`/ait_buildings/${buildingId}/snapshots`);
      const data = await response.json();
      if (response.ok) return { success: true, snapshots: data.snapshots || [] };
      return { success: false, snapshots: [] };
    } catch (err) {
      return { success: false, snapshots: [], error: err.message };
    }
  }, []);

  const shareSnapshot = useCallback(async (buildingId, snapshotId) => {
    try {
      const response = await apiPost(`/ait_buildings/${buildingId}/snapshots/${snapshotId}/share`);
      const data = await response.json();
      if (response.ok) {
        const share_url = `${window.location.origin}/shared/snapshots/${data.share_token}`;
        return { success: true, share_url, expires_at: data.expires_at };
      }
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const exportSnapshotPdf = useCallback(async (buildingId, snapshotId) => {
    try {
      const response = await apiGet(`/ait_buildings/${buildingId}/snapshots/${snapshotId}/export_pdf`);
      if (response.ok) {
        const blob = await response.blob();
        return { success: true, blob };
      }
      return { success: false };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Property Members & Invitations ──

  const fetchPropertyMembers = useCallback(async (propertyId) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/members`);
      if (response.ok) {
        const data = await response.json();
        return { success: true, members: data.members || [] };
      }
      return { success: false, members: [] };
    } catch {
      return { success: false, members: [] };
    }
  }, []);

  const fetchPropertyInvitations = useCallback(async (propertyId) => {
    try {
      const response = await apiGet(`/ait_properties/${propertyId}/invitations`);
      if (response.ok) {
        const data = await response.json();
        return { success: true, invitations: data.invitations || [] };
      }
      return { success: false, invitations: [] };
    } catch {
      return { success: false, invitations: [] };
    }
  }, []);

  const invitePropertyMember = useCallback(async (propertyId, { email, role, invite_type }) => {
    try {
      const body = { role: role || 'viewer' };
      if (invite_type === 'link') {
        body.invite_type = 'link';
      } else {
        body.email = email;
      }
      const response = await apiPost(`/ait_properties/${propertyId}/invitations`, body);
      const data = await response.json();
      if (response.ok) return { success: true, invitation: data.invitation };
      return { success: false, error: data.error || data.errors?.[0] };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const removePropertyMember = useCallback(async (propertyId, memberId) => {
    try {
      const response = await apiDelete(`/ait_properties/${propertyId}/members/${memberId}`);
      if (response.ok) return { success: true };
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const revokePropertyInvitation = useCallback(async (propertyId, invitationId) => {
    try {
      const response = await apiDelete(`/ait_properties/${propertyId}/invitations/${invitationId}`);
      if (response.ok) return { success: true };
      const data = await response.json();
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  // ── Portfolio Analytics ──

  const fetchPortfolioAnalytics = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const response = await apiGet('/ait_portfolio_analytics');
      const data = await response.json();
      if (response.ok) {
        setPortfolioData(data);
        return data;
      }
      return null;
    } catch {
      return null;
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  const fetchPortfolioTrends = useCallback(async (months = 12) => {
    try {
      const response = await apiGet(`/ait_portfolio_analytics/trends?months=${months}`);
      const data = await response.json();
      if (response.ok) return data.trends;
      return [];
    } catch {
      return [];
    }
  }, []);

  const fetchOperationalMetrics = useCallback(async (months = 12) => {
    try {
      const response = await apiGet(`/ait_portfolio_analytics/operational?months=${months}`);
      const data = await response.json();
      if (response.ok) return data;
      return null;
    } catch {
      return null;
    }
  }, []);

  const updatePortfolioPreference = useCallback(async (propertyId, excluded) => {
    try {
      const response = await apiPatch('/ait_portfolio_analytics/preferences', {
        property_id: propertyId, excluded
      });
      const data = await response.json();
      if (response.ok) return { success: true };
      return { success: false, error: data.error };
    } catch (err) {
      return { success: false, error: err.message };
    }
  }, []);

  const value = {
    // State
    properties, currentProperty, currentBuilding, units, calculation, loading, error,
    // Chat state
    chats, currentChatId, chatMessages, chatLoading,
    // Properties
    fetchProperties, createProperty, updateProperty, deleteProperty, fetchProperty,
    // Buildings
    createBuilding, fetchBuilding, updateBuilding, deleteBuilding,
    // Units
    fetchUnits, createUnit, updateUnit, deleteUnit, bulkImportUnits,
    // Designation operations
    redesignateUnit, convertToMarketRate, convertToQualified, swapUnits,
    // Calculation
    calculateBuilding, modelScenario, createSnapshot, evaluateOverIncome,
    // Upload
    parseUpload, parseTicUpload, parseTicBatch, saveUpload, deleteUpload, importUnits, fetchLeasingReport, fetchPropertyAnalytics, fetchHistory,
    // Income limit lookup
    lookupIncomeLimit,
    // Chat
    fetchChats, createChat, fetchChatMessages, sendChatMessage, deleteChat,
    // Monthly designations
    fetchMonthlyDesignations, updateMonthlyDesignations, carryForwardDesignations,
    // Snapshots
    fetchSnapshots, shareSnapshot, exportSnapshotPdf,
    // Members & invitations
    fetchPropertyMembers, fetchPropertyInvitations, invitePropertyMember, removePropertyMember, revokePropertyInvitation,
    // Portfolio analytics
    portfolioData, portfolioLoading, fetchPortfolioAnalytics, fetchPortfolioTrends, fetchOperationalMetrics, updatePortfolioPreference,
    // Setters
    setCurrentProperty, setCurrentBuilding, setCalculation, setCurrentChatId, setChatMessages,
  };

  return (
    <AitContext.Provider value={value}>
      {children}
    </AitContext.Provider>
  );
};
